/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { usePlanwerte, useTagebuchEintraege } from '@/domain/controlling/hooks';
import { useReducer } from 'react';
import { useRouteParams } from '../useRouteParams';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { PlanwerteEditForm, PlanwerteFormReducerAction } from './types';
import {
  getDefaultAbsatzminuten,
  getDefaultAbsatzPlanwert,
  getDefaultAbsatzProduktivitaet,
  getDefaultAbsatzPrognose,
  getDefaultUmsatzPlanwert,
  getDefaultUmsatzProduktivitaet,
  getDefaultUmsatzPrognose,
  getDefaultUmsatzZielminuten,
} from '../helpers';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function usePlanwerteFormReducer() {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { searchParams, routeParams } = useRouteParams();
  const { planwerte } = usePlanwerte({
    standortId,
    jahrMonat: {
      jahr: routeParams.jahr,
      monat: routeParams.monat,
    },
  });
  const { tagebuchEinstellungen } = useTagebuchEinstellungen(standortId);
  const { tagebuchEintraege } = useTagebuchEintraege({
    standortId,
    jahrMonat: {
      jahr: routeParams.jahr,
      monat: routeParams.monat,
    },
  });
  const currentPlanwert = planwerte.find(
    (absatzPlanwert) => absatzPlanwert.datum === searchParams.datum,
  );

  const tagebuchEintragByDatum = tagebuchEintraege.find(
    (tagebuchEintrag) => tagebuchEintrag.datum === searchParams.datum,
  );

  const initalState: PlanwerteEditForm = {
    formValues: {
      absatzPlan: getDefaultAbsatzPlanwert(currentPlanwert.absatzPlan),
      absatzProduktivitaet: getDefaultAbsatzProduktivitaet(
        tagebuchEinstellungen.absatzProduktivitaet,
        currentPlanwert.absatzPlan,
      ),
      absatzPrognose: getDefaultAbsatzPrognose(currentPlanwert.absatzPlan),
      absatzZielminuten: getDefaultAbsatzminuten(currentPlanwert.absatzPlan),
      umsatzPlan: getDefaultUmsatzPlanwert(currentPlanwert.umsatzPlan),
      umsatzProduktivitaet: getDefaultUmsatzProduktivitaet(
        tagebuchEinstellungen.umsatzProduktivitaet,
        currentPlanwert.umsatzPlan,
      ),
      umsatzPrognose: getDefaultUmsatzPrognose(currentPlanwert.umsatzPlan),
      umsatzZielminuten: getDefaultUmsatzZielminuten(
        currentPlanwert.umsatzPlan,
      ),
      interneNotiz: tagebuchEintragByDatum.interneNotiz,
    },
    hasChanges: false,
    isSubmitting: false,
  };

  function planwerteReducer(
    state: PlanwerteEditForm,
    action: PlanwerteFormReducerAction,
  ): PlanwerteEditForm {
    switch (action.type) {
      case 'updateAbsatzPlan': {
        return {
          formValues: {
            ...state.formValues,
            absatzPlan: action.values.absatzPlan,
          },
          hasChanges:
            action.values.absatzPlan !== initalState.formValues.absatzPlan,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateUmsatzPlan': {
        return {
          formValues: {
            ...state.formValues,
            umsatzPlan: action.values.umsatzPlan,
          },
          hasChanges:
            action.values.umsatzPlan !== initalState.formValues.umsatzPlan,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateAbsatzProduktivitaet': {
        return {
          formValues: {
            ...state.formValues,
            absatzProduktivitaet: action.values.absatzProduktivitaet,
          },
          hasChanges:
            action.values.absatzProduktivitaet !==
            initalState.formValues.absatzProduktivitaet,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateUmsatzProduktivitaet': {
        return {
          formValues: {
            ...state.formValues,
            umsatzProduktivitaet: action.values.umsatzProduktivitaet,
          },
          hasChanges:
            action.values.umsatzProduktivitaet !==
            initalState.formValues.umsatzProduktivitaet,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'submit': {
        return {
          ...state,
          isSubmitting: true,
        };
      }
      case 'updateSubmit': {
        return {
          ...state,
          isSubmitting: action.isSubmitting,
        };
      }
      case 'updateDialogMessage': {
        return {
          ...state,
          isSubmitting: false,
          dialogMessage: action.dialogMessage,
        };
      }
      case 'updateInterneNotiz': {
        return {
          ...state,
          formValues: {
            ...state.formValues,
            interneNotiz: action.values.interneNotiz,
          },
          hasChanges:
            state.formValues.interneNotiz !== action.values.interneNotiz,
        };
      }
      case 'reset': {
        return initalState;
      }
    }
  }

  return useReducer(planwerteReducer, initalState);
}
